import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import momentTimezone from 'moment-timezone';

dayjs.extend(utc);

const US_STANDARD_TIME_UTC = [
  {
    utc: 'UTC-5',
    name: 'EST',
  },
  {
    utc: 'UTC-6',
    name: 'CST',
  },
  {
    utc: 'UTC-7',
    name: 'MST',
  },
  {
    utc: 'UTC-8',
    name: 'PST',
  },
  {
    utc: 'UTC-9',
    name: 'AKST',
  },
  {
    utc: 'UTC-10',
    name: 'HAST',
  },
];

const US_DAYLIGHT_SAVING_TIME_UTC = [
  {
    utc: 'UTC-4',
    name: 'EDT',
  },
  {
    utc: 'UTC-5',
    name: 'CDT',
  },
  {
    utc: 'UTC-6',
    name: 'MDT',
  },
  {
    utc: 'UTC-7',
    name: 'PDT',
  },
  {
    utc: 'UTC-8',
    name: 'AKDT',
  },
  {
    utc: 'UTC-9',
    name: 'HADT',
  },
];

/**
 * Trims padding zeros out of a timezone label so offsets read as 'GMT+5' rather than 'GMT+05'. Drops a '+00' segment when present, otherwise strips every '0' character, and returns an empty string for a missing label.
 *
 * @param timezone - Timezone label to condense.
 * @returns Condensed timezone label, or an empty string.
 */
const removeZeroTimezone = (timezone?: string) => {
  if (!timezone) {
    return '';
  }
  if (timezone.includes('+00')) {
    return timezone.replaceAll('+00', '');
  }
  if (timezone.includes('0')) {
    return timezone.replaceAll('0', '');
  }
  return timezone;
};

/**
 * Shifts a date by the study's hour offset, or back to server time when reverting.
 * Clones the date, negates the offset when shouldRevertToServerTime is set, then adds positive
 * offsets or subtracts negative ones in hours, leaving the clone unchanged for a zero offset.
 *
 * @param props - The date to shift, the study time offset in hours and the revert-to-server flag.
 * @returns The shifted copy of the date.
 */
const adjustStudyTime = ({
  dateMoment,
  studyTimeOffset = 0,
  shouldRevertToServerTime = false,
}: {
  dateMoment: Dayjs | moment.Moment;
  studyTimeOffset?: number;
  shouldRevertToServerTime?: boolean;
}) => {
  let newDateMoment: any = dateMoment.clone();
  let newStudyTimeOffset = studyTimeOffset;
  if (shouldRevertToServerTime) {
    newStudyTimeOffset *= -1;
  }
  if (newStudyTimeOffset) {
    if (newStudyTimeOffset > 0) {
      newDateMoment = newDateMoment.add(newStudyTimeOffset, 'hours');
    } else {
      newDateMoment = newDateMoment.subtract(Math.abs(newStudyTimeOffset), 'hours');
    }
  }
  return newDateMoment;
};

/**
 * Builds the display timezone label for a study date, accounting for US daylight saving time.
 * Returns the condensed raw timezone outside US facilities, otherwise shifts the UTC offset by an
 * hour when the date and the study start fall on different sides of DST and looks the result up
 * in the US daylight-saving or standard tables, falling back to the condensed raw timezone.
 *
 * @param props - The study start date, the date to label, its timezone, the study time offset and
 *   the facility with its country and IANA timezone.
 * @returns The condensed timezone label to display.
 */
const formatTimezone = ({
  startStudyDate,
  isoDate,
  timezone,
  studyTimeOffset,
  facility,
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facility?: {
    contact?: {
      country?: string;
    };
    timezone?: string;
  };
}) => {
  if (!facility || facility.contact?.country !== 'US' || !isoDate) {
    return removeZeroTimezone(timezone);
  }

  if (startStudyDate) {
    const isStartStudyDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(startStudyDate, facility.timezone as string),
      studyTimeOffset,
    }).isDST();
    const isIsoDateDST = momentTimezone.tz(isoDate, facility.timezone as string).isDST();
    let newTimezone: string | number = _.toNumber(_.split(timezone, 'UTC')[1]);
    if (!isStartStudyDateDST && isIsoDateDST) {
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      newTimezone -= 1;
    }
    const newTimezoneString = `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`;
    /**
     * Finds the UTC timezone descriptor whose utc field matches newTimezoneString, selecting the daylight-saving list when isIsoDateDST is set and the standard list otherwise. Derived via a lodash find over the two US timezone offset tables.
     *
     * @returns The matching timezone entry, or undefined when none matches.
     */
    const utcEntry = _.find(
      isIsoDateDST ? US_DAYLIGHT_SAVING_TIME_UTC : US_STANDARD_TIME_UTC,
      (x) => x.utc === newTimezoneString,
    );
    if (utcEntry) {
      return removeZeroTimezone(utcEntry.name);
    }
  }

  const isDST = momentTimezone.tz(isoDate, facility.timezone as string).isDST();
  /**
   * Finds the UTC timezone descriptor whose utc field matches the timezone value, selecting the daylight-saving list when isDST is set and the standard list otherwise. Derived via a lodash find over the two US timezone offset tables.
   *
   * @returns The matching timezone entry, or undefined when none matches.
   */
  const utcEntry = _.find(
    isDST ? US_DAYLIGHT_SAVING_TIME_UTC : US_STANDARD_TIME_UTC,
    (x) => x.utc === timezone,
  );
  if (utcEntry) {
    return removeZeroTimezone(utcEntry.name);
  }
  return removeZeroTimezone(timezone);
};

/**
 * Converts a 'GMT+hh' style timezone label into an offset in minutes. Parses the numeric part after the first three characters and multiplies it by 60, falling back to zero if parsing throws.
 *
 * @param timezone - Timezone label whose hour offset is parsed.
 * @returns Offset in minutes, or zero when the label could not be parsed.
 */
const timezoneToOffset = (timezone?: string | null) => {
  if (!timezone) {
    return 0;
  }
  try {
    const timeString = timezone.substring(3);
    return parseInt(timeString, 10) * 60;
  } catch {
    return 0;
  }
};

/**
 * Computes the UTC offset to render a study date with, correcting for US daylight saving changes.
 * Parses the offset from the timezone label and, for US facilities with a study start date, adds
 * or removes an hour when the date and the study start sit on different sides of DST in the
 * facility timezone.
 *
 * @param props - The study start date, the date to render, its timezone, the study time offset,
 *   the facility country and the facility IANA timezone.
 * @returns An object carrying the offset in minutes and the adjusted UTC timezone label.
 */
const getUtcOffsetWithDst = ({
  startStudyDate,
  isoDate,
  timezone,
  studyTimeOffset,
  facilityCountry,
  facilityTimezone = 'America/New_York',
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  let utcOffset = timezoneToOffset(timezone);
  let newTimezone = _.toNumber(_.split(timezone, 'UTC')[1]);
  if (startStudyDate && facilityCountry === 'US') {
    const isStartStudyDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(startStudyDate, facilityTimezone),
      studyTimeOffset,
    }).isDST();
    const isIsoDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(isoDate, facilityTimezone),
      studyTimeOffset,
    }).isDST();
    if (!isStartStudyDateDST && isIsoDateDST) {
      // *: From standard to DST
      utcOffset += 60;
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      // *: From DST to standard
      utcOffset -= 60;
      newTimezone -= 1;
    }
    // *: 2 dates are in the same time zone range or facility is in time zone that do not observe DST, do nothing
  }
  return {
    utcOffset,
    newTimezone: `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`,
  };
};

/**
 * Formats a study date as text in the study's own timezone.
 * Applies the DST-corrected UTC offset and the study time offset to the date, then formats it with
 * the given pattern, returning an empty string when the date is missing or invalid.
 *
 * @param props - The study start date, the date to format, its timezone, the study time offset,
 *   the facility country and timezone, and the output format.
 * @returns The formatted date, or an empty string.
 */
const dateToString = ({
  startStudyDate,
  isoDate,
  timezone = 'UTC+0',
  studyTimeOffset = 0,
  facilityCountry,
  facilityTimezone,
  format = 'MM/DD/YYYY, HH:mm:ss',
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string | undefined;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
  format?: string;
}) => {
  if (isoDate) {
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    });
    let dateMoment = dayjs(isoDate).utcOffset(utcOffset);
    dateMoment = adjustStudyTime({
      dateMoment,
      studyTimeOffset,
    });
    return dateMoment.isValid() ? dateMoment.format(format) : '';
  }
  return '';
};

export { dateToString, formatTimezone };
